import HelpCenterCard from '@/components/HelpCenterCard';
import HelpCenterSearch from '@/components/HelpCenterSearch';
import SEOHead from '@/components/SEOHead';
import { checkSanityConnection, client } from '@/utils/sanity-client';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Empty, Result, Row, Spin } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const HelpCenter = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const fetchDocuments = async (isRetry = false) => {
    if (isRetry) {
      setRetryCount((prev) => prev + 1);
    }

    setLoading(true);
    setError(null);

    try {
      // Check connection first
      console.log('[HelpCenter] Checking Sanity connection...');
      const isConnected = await checkSanityConnection();
      setConnectionStatus(isConnected);

      if (!isConnected) {
        throw new Error('Unable to connect to the content server');
      }

      console.log('[HelpCenter] Fetching documents...');
      const query = `*[_type in ["articles", "faq", "caseStudies", "guides", "industryNews"]] | order(coalesce(title, question) asc) {
        _id,
        _type,
        title,
        question,
        answer,
        "slug": slug.current,
        "preview": select(
          defined(body) => pt::text(body[0...150]),
          defined(answer) => answer[0...150],
          ''
        ),
        body,
        definition,
        "estimatedReadTime": round(length(pt::text(body)) / 1000 * 0.2),
        _createdAt
      }`;

      const data = await client.fetch(query);
      console.log('[HelpCenter] Documents fetched successfully:', {
        count: data?.length || 0,
        timestamp: new Date().toISOString(),
      });

      setDocuments(data || []);
      setError(null);
    } catch (error) {
      console.error('[HelpCenter] Error fetching documents:', {
        error: {
          message: error.message,
          stack: error.stack,
        },
        retryCount,
        timestamp: new Date().toISOString(),
      });

      setError(error.message || 'Failed to load help center content');

      // If we haven't exceeded max retries, try again after a delay
      if (retryCount < 2) {
        console.log(`[HelpCenter] Scheduling retry ${retryCount + 1} in 2 seconds...`);
        setTimeout(() => {
          fetchDocuments(true);
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleSearch = (searchValue) => {
    // If searchValue is an object (from AutoComplete selection), use its value property
    const value = typeof searchValue === 'object' ? searchValue.value || '' : searchValue || '';
    setSearchText(value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleRetry = () => {
    setRetryCount(0);
    fetchDocuments();
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesCategory = selectedCategory === 'all' || doc._type === selectedCategory;
    const searchQuery = String(searchText || '').toLowerCase();
    const searchableContent = [doc.title, doc.question, doc.answer, doc.preview, doc.definition]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const matchesSearch = !searchQuery || searchableContent.includes(searchQuery);
    return matchesCategory && matchesSearch;
  });

  if (!connectionStatus) {
    return (
      <Result
        status="error"
        title="Connection Error"
        subTitle={
          <div>
            <p>Unable to connect to the content server. This might be due to:</p>
            <ul style={{ textAlign: 'left', maxWidth: '400px', margin: '20px auto' }}>
              <li>Temporary server unavailability</li>
              <li>Network connectivity issues</li>
              <li>Browser security settings</li>
            </ul>
          </div>
        }
        extra={[
          <Button
            key="retry"
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRetry}
            loading={loading}
          >
            Retry Connection
          </Button>,
        ]}
      />
    );
  }

  return (
    <div style={{ minHeight: 'calc(100vh - 100px)' }}>
      <SEOHead />

      <section className="section hero v4" style={{ paddingTop: '132px', paddingBottom: '0' }}>
        <div className="container-default w-container">
          <div className="z-index-1">
            <div className="mg-bottom-24px">
              <div className="inner-container center">
                <div className="inner-container _861px---tablet center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        BRAND YOUR EMAILS. GET MORE OPENS.
                      </div>
                    </div>
                    <h1 className="mg-bottom-16px">Brandymail™ Help Center</h1>
                    <p className="color-neutral-600">
                      Get expert guidance on email branding, BIMI implementation, and VMC validation
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="position-absolute card-top-bg---bottom pricing-bg" />
      </section>

      <div className="overflow-hidden" style={{ minHeight: 'calc(100vh - 300px)' }}>
        <section className="section pd-top-0px bg-neutral-100">
          <div className="container-default w-container">
            <div className="inner-container center" style={{ maxWidth: '720px' }}>
              <div className="mg-bottom-48px">
                <HelpCenterSearch onSearch={handleSearch} onCategoryChange={handleCategoryChange} />
              </div>
            </div>

            {error && (
              <Alert
                message={
                  <span>
                    <WarningOutlined style={{ marginRight: 8 }} />
                    Error Loading Content
                  </span>
                }
                description={error}
                type="error"
                showIcon
                action={
                  <Button
                    size="small"
                    type="primary"
                    onClick={handleRetry}
                    loading={loading}
                    icon={<ReloadOutlined />}
                  >
                    Try Again
                  </Button>
                }
                style={{ marginBottom: '24px' }}
              />
            )}

            <motion.div layout>
              <AnimatePresence mode="popLayout">
                {loading ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 0',
                      minHeight: 'calc(100vh - 500px)',
                    }}
                  >
                    <Spin size="large" />
                    <div style={{ marginTop: 16, color: '#666' }}>
                      Loading help center content...
                    </div>
                  </div>
                ) : filteredDocuments.length > 0 ? (
                  <Row gutter={[24, 24]}>
                    {filteredDocuments.map((doc, index) => (
                      <Col xs={24} sm={12} lg={8} key={doc._id}>
                        <motion.div
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          <HelpCenterCard item={doc} type={doc._type} />
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Empty
                      description={searchText ? 'No results found' : 'No content available'}
                      style={{ margin: '48px 0' }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HelpCenter;
